import {
  HomeFlowDefinition,
  HomeFunnelState,
  HomeFlowType,
  HomeStepType,
  HomeFormAnswer,
} from 'scenes/home/types';
import {
  hasBuildingCoverType,
  hasClaimsMade,
  hasContentsCoverType,
  isInvestmentProperty,
  isPropertyOwned,
  isPropertyUsedForBusiness,
  notInvestmentProperty,
  usingAgreedSumInsured,
  usingEstimatedSumInsured,
} from 'scenes/home/selectors/quoteSelectors';
import config, { FeatureToggleState } from 'shared/config';

export const forwardLinks: HomeFlowDefinition = {
  [HomeStepType.CoverTypeSelection]: { step: HomeStepType.PolicyStartDate },
  [HomeStepType.PolicyStartDate]: { step: HomeStepType.RiskAddress },
  [HomeStepType.RiskAddress]: { step: HomeStepType.Ownership },
  [HomeStepType.Ownership]: (funnelState: HomeFunnelState) => ({
    step: isPropertyOwned(funnelState)
      ? HomeStepType.PropertyPurpose
      : HomeStepType.PropertyUsedForBusiness,
  }),
  [HomeStepType.PropertyPurpose]: (funnelState: HomeFunnelState) => ({
    step: isInvestmentProperty(funnelState)
      ? HomeStepType.KickOutChoice
      : HomeStepType.PropertyUsedForBusiness,
  }),
  [HomeStepType.KickOutChoice]: (funnelState: HomeFunnelState) =>
    notInvestmentProperty(funnelState)
      ? { step: HomeStepType.PropertyPurpose }
      : {},
  [HomeStepType.PropertyUsedForBusiness]: (funnelState: HomeFunnelState) => ({
    step: isPropertyUsedForBusiness(funnelState)
      ? HomeStepType.PropertyBusinessType
      : HomeStepType.PersonalOrBusinessPolicy,
  }),
  [HomeStepType.PropertyBusinessType]: {
    step: HomeStepType.PersonalOrBusinessPolicy,
  },
  [HomeStepType.PersonalOrBusinessPolicy]: {
    step: HomeStepType.ContactDateOfBirth,
  },
  [HomeStepType.ContactDateOfBirth]: { step: HomeStepType.RecentInsurer },
  [HomeStepType.RecentInsurer]: { step: HomeStepType.YearsInsured },
  [HomeStepType.YearsInsured]: { step: HomeStepType.ClaimsMade },
  [HomeStepType.ClaimsMade]: (funnelState: HomeFunnelState) => ({
    step: hasClaimsMade(funnelState)
      ? HomeStepType.BreakInAndAdditionalSecurity
      : HomeStepType.BuildingType,
  }),
  [HomeStepType.BreakInAndAdditionalSecurity]: {
    step: HomeStepType.BuildingType,
  },
  [HomeStepType.BuildingType]: { step: HomeStepType.NumberOfBedrooms },
  [HomeStepType.NumberOfBedrooms]: { step: HomeStepType.PropertyBuiltYear },
  [HomeStepType.PropertyBuiltYear]: { step: HomeStepType.RoofMaterial },
  [HomeStepType.RoofMaterial]: { step: HomeStepType.WallMaterial },
  [HomeStepType.WallMaterial]: (funnelState: HomeFunnelState) => ({
    step: hasContentsCoverType(funnelState)
      ? HomeStepType.SecurityFeatures
      : HomeStepType.SumInsuredChoice,
  }),
  [HomeStepType.SecurityFeatures]: (funnelState: HomeFunnelState) => ({
    step: hasBuildingCoverType(funnelState)
      ? HomeStepType.SumInsuredChoice
      : HomeStepType.SumInsuredContentsValue,
  }),
  [HomeStepType.SumInsuredChoice]: (funnelState: HomeFunnelState) => {
    if (!usingEstimatedSumInsured(funnelState)) {
      return { step: HomeStepType.SumInsuredBuildingValue };
    }
    return {
      step: hasContentsCoverType(funnelState)
        ? HomeStepType.SumInsuredContentsValue
        : HomeStepType.FlybuysNumber,
    };
  },
  [HomeStepType.SumInsuredBuildingValue]: (funnelState: HomeFunnelState) => ({
    step: hasContentsCoverType(funnelState)
      ? HomeStepType.SumInsuredContentsValue
      : HomeStepType.FlybuysNumber,
  }),
  [HomeStepType.SumInsuredContentsValue]: { step: HomeStepType.FlybuysNumber },
  [HomeStepType.FlybuysNumber]:
    config.featureToggles.skipHomePromoPage === FeatureToggleState.ON
      ? {
          step: HomeStepType.QuotePrice,
          flow: HomeFlowType.CoverOptions,
        }
      : { step: HomeStepType.PromoCode },
  [HomeStepType.PromoCode]: {
    step: HomeStepType.QuotePrice,
    flow: HomeFlowType.CoverOptions,
  },
};

export const backwardLinks: HomeFlowDefinition = {
  [HomeStepType.CoverTypeSelection]: {},
  [HomeStepType.PolicyStartDate]: { step: HomeStepType.CoverTypeSelection },
  [HomeStepType.RiskAddress]: { step: HomeStepType.PolicyStartDate },
  [HomeStepType.Ownership]: { step: HomeStepType.RiskAddress },
  [HomeStepType.PropertyUsedForBusiness]: (funnelState: HomeFunnelState) => ({
    step: isPropertyOwned(funnelState)
      ? HomeStepType.PropertyPurpose
      : HomeStepType.Ownership,
  }),
  [HomeStepType.PropertyBusinessType]: {
    step: HomeStepType.PropertyUsedForBusiness,
  },
  [HomeStepType.PropertyPurpose]: { step: HomeStepType.Ownership },
  [HomeStepType.KickOutChoice]: { step: HomeStepType.PropertyPurpose },
  [HomeStepType.PersonalOrBusinessPolicy]: (funnelState: HomeFunnelState) => ({
    step: isPropertyUsedForBusiness(funnelState)
      ? HomeStepType.PropertyBusinessType
      : HomeStepType.PropertyUsedForBusiness,
  }),
  [HomeStepType.ContactDateOfBirth]: {
    step: HomeStepType.PersonalOrBusinessPolicy,
  },
  [HomeStepType.RecentInsurer]: { step: HomeStepType.ContactDateOfBirth },
  [HomeStepType.YearsInsured]: { step: HomeStepType.RecentInsurer },
  [HomeStepType.ClaimsMade]: { step: HomeStepType.YearsInsured },
  [HomeStepType.BreakInAndAdditionalSecurity]: {
    step: HomeStepType.ClaimsMade,
  },
  [HomeStepType.BuildingType]: (funnelState: HomeFunnelState) => ({
    step: hasClaimsMade(funnelState)
      ? HomeStepType.BreakInAndAdditionalSecurity
      : HomeStepType.ClaimsMade,
  }),
  [HomeStepType.NumberOfBedrooms]: { step: HomeStepType.BuildingType },
  [HomeStepType.PropertyBuiltYear]: { step: HomeStepType.NumberOfBedrooms },
  [HomeStepType.RoofMaterial]: { step: HomeStepType.PropertyBuiltYear },
  [HomeStepType.WallMaterial]: { step: HomeStepType.RoofMaterial },
  [HomeStepType.SecurityFeatures]: { step: HomeStepType.WallMaterial },
  [HomeStepType.SumInsuredChoice]: (funnelState: HomeFunnelState) => ({
    step: hasContentsCoverType(funnelState)
      ? HomeStepType.SecurityFeatures
      : HomeStepType.WallMaterial,
  }),
  [HomeStepType.SumInsuredBuildingValue]: {
    step: HomeStepType.SumInsuredChoice,
  },
  [HomeStepType.SumInsuredContentsValue]: (funnelState: HomeFunnelState) => {
    if (hasBuildingCoverType(funnelState)) {
      return {
        step: usingAgreedSumInsured(funnelState)
          ? HomeStepType.SumInsuredBuildingValue
          : HomeStepType.SumInsuredChoice,
      };
    }
    return {
      step: HomeStepType.SecurityFeatures,
    };
  },
  [HomeStepType.FlybuysNumber]: (funnelState: HomeFunnelState) => {
    if (hasContentsCoverType(funnelState)) {
      return { step: HomeStepType.SumInsuredContentsValue };
    }
    return {
      step: usingAgreedSumInsured(funnelState)
        ? HomeStepType.SumInsuredBuildingValue
        : HomeStepType.SumInsuredChoice,
    };
  },
  [HomeStepType.PromoCode]: { step: HomeStepType.FlybuysNumber },
};

export const editQuoteDetails = (
  funnelState: HomeFunnelState,
  action: HomeFormAnswer
) => ({
  step: action.targetStep,
  flow: HomeFlowType.QuoteDetails,
});
